import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import TaskList from './components/TaskList';
import Calendar from './components/Calendar';
import Appointments from './components/Appointments';
import LessonPlanner from './components/LessonPlanner';
import Dashboard from './components/Dashboard';
import TeamDashboard from './components/TeamDashboard';
import Units from './components/Units';
import Lessons from './components/Lessons';
import Classes from './components/Classes';
import Classrooms from './components/Classrooms';
import WeeklySchedule from './components/WeeklySchedule';
import PlannerCalendar from './components/PlannerCalendar';
import ScopeAndSequence from './components/ScopeAndSequence';
import UserManagement from './components/UserManagement';
import PointTracker from './components/PointTracker';
import Assignments from './components/Assignments';
import DailySummaryManager from './components/DailySummaryManager';
import DailySummary from './components/DailySummary';
import BigScreenView from './components/BigScreenView';
import HostAvailability from './components/HostAvailability';
import UserProfile from './components/UserProfile';
import PasswordChangeModal from './components/PasswordChangeModal';
import { Users, Calendar as CalendarIcon } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase/config';
import { getEvents } from './firebase/calendarService';
import { getUsers } from './firebase/userService';
import { getTeamTasks, createTeamTask, deleteTeamTask, toggleTaskCompletion } from './firebase/teamService';
import { getTeamMessages, createTeamMessage, deleteTeamMessage } from './firebase/teamService';
import { getTeamDocuments, createTeamDocument, deleteTeamDocument } from './firebase/teamService';
import { uploadTeamDocument, deleteResourceFile } from './firebase/storageService';
import { isAdmin, canAccessLessonPlanner, canAccessTaskManager, canAccessDailySummary, canAccessTeamDashboard, canSeeCurriculum, canDeleteAnyTask, canDeleteCompletedTask } from './utils/permissions';
import './App.css';
import './components/PlannerCalendar.css';

function Home() {
  const { currentUser, logout, login } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [classes, setClasses] = useState([]);
  const [units, setUnits] = useState([]);
  const [weeklyLessons, setWeeklyLessons] = useState({});
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to parse date string as local time
  const parseDateLocal = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [currentUserData, setCurrentUserData] = useState(null);

  // Team Hub state
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      if (currentUser?.email) {
        const usersResult = await getUsers();
        if (usersResult.success) {
          const userData = usersResult.users.find(u => u.email?.toLowerCase() === currentUser.email.toLowerCase());
          setCurrentUserData(userData);

          // Load team data if user has access
          if (canAccessTeamDashboard(userData)) {
            loadTeamData();
          }
        }
      } else {
        setCurrentUserData(null);
        setTasks([]);
        setMessages([]);
      }
    };

    loadUserData();
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch all classes, units, lessons, and events (public access)
      const [classesSnap, unitsSnap, lessonsSnap, eventsSnap, calendarEventsResult] = await Promise.all([
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'units')),
        getDocs(collection(db, 'lessons')),
        getDocs(collection(db, 'events')),
        getEvents(null) // Get calendar events
      ]);

      const classesData = [];
      classesSnap.forEach(doc => classesData.push({ id: doc.id, ...doc.data() }));

      const unitsData = [];
      unitsSnap.forEach(doc => unitsData.push({ id: doc.id, ...doc.data() }));

      const lessonsData = [];
      lessonsSnap.forEach(doc => lessonsData.push({ id: doc.id, ...doc.data() }));

      const eventsData = [];
      eventsSnap.forEach(doc => eventsData.push({ id: doc.id, ...doc.data() }));

      setClasses(classesData);
      setUnits(unitsData);

      // Filter events for next 2 weeks
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fourWeeksFromNow = new Date(today);
      fourWeeksFromNow.setDate(today.getDate() + 28);

      const upcoming = eventsData.filter(event => {
        if (!event.date) return false;
        const eventDate = typeof event.date === 'string'
          ? new Date(event.date)
          : event.date.toDate ? event.date.toDate() : null;

        if (!eventDate) return false;
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today && eventDate <= fourWeeksFromNow;
      }).sort((a, b) => {
        const dateA = typeof a.date === 'string' ? new Date(a.date) : a.date.toDate();
        const dateB = typeof b.date === 'string' ? new Date(b.date) : b.date.toDate();
        return dateA - dateB;
      });

      setUpcomingEvents(upcoming);

      // Process calendar events
      if (calendarEventsResult.success && calendarEventsResult.events) {
        const calEvents = calendarEventsResult.events;
        console.log('Calendar events fetched:', calEvents);

        // Filter calendar events for next 4 weeks
        const upcomingCalEvents = calEvents.filter(event => {
          if (!event.date) {
            console.log('Event has no date:', event);
            return false;
          }

          // Parse dates as local time
          const eventStartDate = parseDateLocal(event.date);
          const eventEndDate = event.endDate ? parseDateLocal(event.endDate) : eventStartDate;

          if (isNaN(eventStartDate.getTime())) {
            console.log('Invalid event date:', event.date);
            return false;
          }

          eventStartDate.setHours(0, 0, 0, 0);
          eventEndDate.setHours(0, 0, 0, 0);

          // Event is upcoming if it starts before the end of our window and ends after today
          const isUpcoming = eventStartDate <= fourWeeksFromNow && eventEndDate >= today;
          console.log('Event:', event.title, 'Start:', event.date, 'End:', event.endDate || event.date, 'Is upcoming:', isUpcoming);
          return isUpcoming;
        }).sort((a, b) => {
          const dateA = parseDateLocal(a.date);
          const dateB = parseDateLocal(b.date);
          return dateA - dateB;
        });

        console.log('Upcoming calendar events:', upcomingCalEvents);
        setCalendarEvents(upcomingCalEvents);
      } else {
        console.log('Calendar events result:', calendarEventsResult);
      }

      // Get current week (Monday through Saturday)
      const currentDay = new Date();
      const dayOfWeek = currentDay.getDay();
      const diff = currentDay.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust to Monday
      const monday = new Date(currentDay.setDate(diff));
      monday.setHours(0, 0, 0, 0);

      // Create object to store lessons by day
      const weekLessons = {};
      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      for (let i = 0; i < 6; i++) {
        const currentDay = new Date(monday);
        currentDay.setDate(monday.getDate() + i);
        const dayStr = currentDay.toISOString().split('T')[0];

        const dayLessons = lessonsData.filter(lesson => {
          const dateField = lesson.startDate || lesson.scheduledDate;
          if (!dateField) return false;
          const lessonDate = typeof dateField === 'string'
            ? dateField
            : dateField.toDate ? dateField.toDate().toISOString().split('T')[0] : '';
          return lessonDate === dayStr;
        });

        weekLessons[weekdays[i]] = {
          date: currentDay,
          lessons: dayLessons
        };
      }

      // Find tournament events for Saturday (from calendar events)
      const saturdayDate = new Date(monday);
      saturdayDate.setDate(monday.getDate() + 5);
      saturdayDate.setHours(0, 0, 0, 0);

      const sundayDate = new Date(saturdayDate);
      sundayDate.setDate(saturdayDate.getDate() + 1);

      let weekendTournaments = [];
      if (calendarEventsResult.success && calendarEventsResult.events) {
        weekendTournaments = calendarEventsResult.events.filter(event => {
          if (!event.type || !event.type.includes('tournament')) return false;
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          // Check if event is on Saturday or Sunday
          return (eventDate.toDateString() === saturdayDate.toDateString() ||
                  eventDate.toDateString() === sundayDate.toDateString());
        });
      }

      weekLessons['Saturday'].tournaments = weekendTournaments;

      setWeeklyLessons(weekLessons);

      // Set current day index to today
      const todayIndex = new Date().getDay();
      // Convert Sunday (0) to -1, then add 1 to get Monday = 0, Tuesday = 1, etc.
      const weekdayIndex = todayIndex === 0 ? 5 : todayIndex - 1; // Sunday becomes Saturday (5)
      setCurrentDayIndex(Math.max(0, Math.min(5, weekdayIndex))); // Clamp to 0-5 range
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadTeamData = async () => {
    const tasksResult = await getTeamTasks();
    const messagesResult = await getTeamMessages();
    const documentsResult = await getTeamDocuments();

    if (tasksResult.success) {
      setTasks(tasksResult.tasks);
    }
    if (messagesResult.success) {
      setMessages(messagesResult.messages);
    }
    if (documentsResult.success) {
      setDocuments(documentsResult.documents);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;

    const result = await createTeamTask({
      title: newTask,
      completed: false,
      createdBy: currentUser.uid,
      createdByName: currentUserData?.name || currentUser.email,
      createdByInitials: currentUserData?.initials || currentUser.email.substring(0, 2).toUpperCase()
    });

    if (result.success) {
      setNewTask('');
      loadTeamData();
    }
  };

  const handleCreateMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageData = {
      content: newMessage,
      createdBy: currentUser.uid,
      createdByName: currentUserData?.name || currentUser.email,
      createdByInitials: currentUserData?.initials || currentUser.email.substring(0, 2).toUpperCase(),
      replyTo: replyingTo ? replyingTo.id : null,
      replyToContent: replyingTo ? replyingTo.content : null,
      replyToName: replyingTo ? replyingTo.createdByName : null
    };

    const result = await createTeamMessage(messageData);

    if (result.success) {
      setNewMessage('');
      setReplyingTo(null);
      loadTeamData();
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    const completedBy = !currentStatus ? {
      uid: currentUser.uid,
      name: currentUserData?.name || currentUser.email,
      initials: currentUserData?.initials || currentUser.email.substring(0, 2).toUpperCase()
    } : null;

    const result = await toggleTaskCompletion(taskId, !currentStatus, completedBy);
    if (result.success) {
      loadTeamData();
    }
  };

  const handleDeleteTask = async (task) => {
    const isOwnTask = task.createdBy === currentUser.uid;
    const isCompleted = task.completed;

    // Check permissions
    if (isCompleted && !canDeleteCompletedTask(currentUserData)) {
      alert('Only admins can delete completed tasks');
      return;
    }

    if (!isOwnTask && !canDeleteAnyTask(currentUserData)) {
      alert('You can only delete your own tasks');
      return;
    }

    if (window.confirm('Are you sure you want to delete this task?')) {
      const result = await deleteTeamTask(task.id);
      if (result.success) {
        loadTeamData();
      }
    }
  };

  const handleDeleteMessage = async (message) => {
    const isOwnMessage = message.createdBy === currentUser.uid;

    if (!isOwnMessage && !isAdmin(currentUserData)) {
      alert('You can only delete your own messages');
      return;
    }

    if (window.confirm('Are you sure you want to delete this message?')) {
      const result = await deleteTeamMessage(message.id);
      if (result.success) {
        loadTeamData();
      }
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Organize messages into threads
  const organizeMessageThreads = () => {
    const parentMessages = messages.filter(msg => !msg.replyTo);
    const replyMessages = messages.filter(msg => msg.replyTo);

    return parentMessages.map(parent => ({
      ...parent,
      replies: replyMessages.filter(reply => reply.replyTo === parent.id)
    }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleUploadDocument = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingDocument(true);

    try {
      // Upload file to storage
      const uploadResult = await uploadTeamDocument(file, {
        name: currentUserData?.name || currentUser.email,
        uid: currentUser.uid
      });

      if (uploadResult.success) {
        // Save document metadata to Firestore
        const documentData = {
          fileName: uploadResult.fileName,
          fileUrl: uploadResult.url,
          filePath: uploadResult.filePath,
          uploadedBy: currentUser.uid,
          uploadedByName: currentUserData?.name || currentUser.email
        };

        const result = await createTeamDocument(documentData);
        if (result.success) {
          loadTeamData();
        } else {
          alert('Failed to save document metadata');
        }
      } else {
        alert('Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document');
    } finally {
      setUploadingDocument(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async (document) => {
    // Only admin and officers can delete
    if (!isAdmin(currentUserData) && currentUserData?.role !== 'Officer') {
      alert('Only admins and officers can delete documents');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${document.fileName}"?`)) {
      try {
        // Delete from storage
        const storageResult = await deleteResourceFile(document.filePath);

        // Delete from Firestore
        const firestoreResult = await deleteTeamDocument(document.id);

        if (storageResult.success && firestoreResult.success) {
          loadTeamData();
        } else {
          alert('Failed to delete document');
        }
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Error deleting document');
      }
    }
  };

  const getClassesForLesson = (lesson) => {
    if (!lesson.unitIds || lesson.unitIds.length === 0) {
      if (lesson.unitId) {
        const unit = units.find(u => u.id === lesson.unitId);
        if (unit && unit.classIds) {
          return classes.filter(c => unit.classIds.includes(c.id));
        }
      }
      return [];
    }

    const lessonUnits = units.filter(u => lesson.unitIds.includes(u.id));
    const classIds = new Set();
    lessonUnits.forEach(unit => {
      if (unit.classIds) {
        unit.classIds.forEach(classId => classIds.add(classId));
      }
    });

    return classes.filter(c => classIds.has(c.id));
  };

  const getUnitNames = (lesson) => {
    const unitIds = lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []);
    return unitIds
      .map(id => units.find(u => u.id === id)?.title)
      .filter(Boolean)
      .join(', ');
  };

  const groupLessonsByClass = (lessonsList) => {
    const grouped = {};

    lessonsList.forEach(lesson => {
      const lessonClasses = getClassesForLesson(lesson);

      if (lessonClasses.length === 0) {
        if (!grouped['Unassigned']) {
          grouped['Unassigned'] = { className: 'Unassigned', lessons: [] };
        }
        grouped['Unassigned'].lessons.push(lesson);
      } else {
        lessonClasses.forEach(cls => {
          if (!grouped[cls.id]) {
            grouped[cls.id] = { className: cls.name, lessons: [] };
          }
          if (!grouped[cls.id].lessons.find(l => l.id === lesson.id)) {
            grouped[cls.id].lessons.push(lesson);
          }
        });
      }
    });

    return Object.values(grouped);
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    const success = await login(email, password);
    if (success) {
      setShowLoginModal(false);
      setEmail('');
      setPassword('');
    } else {
      setError('Login failed. Please try again.');
    }
  };

  return (
    <div className="App">
      <header className="hero">
        {/* Quick Links - Left Side */}
        <div className="header-quick-links">
          <a href="https://texasforensicassociation.com" target="_blank" rel="noopener noreferrer" className="header-quick-link" title="Texas Forensic Association">
            <div className="header-link-icon">🏆</div>
            <span>TFA</span>
          </a>
          <a href="https://speechanddebate.org" target="_blank" rel="noopener noreferrer" className="header-quick-link" title="National Speech & Debate Association">
            <div className="header-link-icon">🎤</div>
            <span>NSDA</span>
          </a>
          <a href="https://uiltexas.org" target="_blank" rel="noopener noreferrer" className="header-quick-link" title="UIL Texas">
            <div className="header-link-icon">⭐</div>
            <span>UIL</span>
          </a>
          <a href="https://www.tabroom.com" target="_blank" rel="noopener noreferrer" className="header-quick-link" title="Tabroom.com">
            <div className="header-link-icon">📊</div>
            <span>Tabroom</span>
          </a>
        </div>

        <div className="auth-corner">
          {currentUser ? (
            <>
              <span className="corner-user-name">
                {currentUserData?.name || currentUser.email}
              </span>
              <Link to="/profile" className="corner-profile-button">
                Profile
              </Link>
              {(currentUser.email?.toLowerCase() === 'kasey.willeby@gmail.com' || currentUser.email?.toLowerCase() === 'mr.willeby@gmail.com') && (
                <Link to="/users" className="corner-users-button">
                  👥 Users
                </Link>
              )}
              <button onClick={handleLogout} className="corner-logout-button">
                Logout
              </button>
            </>
          ) : (
            <button onClick={() => setShowLoginModal(true)} className="corner-login-button">
              Login
            </button>
          )}
        </div>
        <div className="hero-content">
          <h1>Mr. Willeby's Class Tools</h1>
        </div>
      </header>

      {showLoginModal && (
        <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoginModal(false)}>×</button>
            <h2>Login</h2>
            <p className="modal-subtitle">Enter your email and password</p>

            {error && <div className="modal-error">{error}</div>}

            <form onSubmit={handleLogin}>
              <div className="modal-form-group">
                <label htmlFor="modal-email">Email</label>
                <input
                  type="email"
                  id="modal-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="modal-form-group">
                <label htmlFor="modal-password">Password</label>
                <input
                  type="password"
                  id="modal-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>

              <button type="submit" className="modal-login-button">
                Sign In
              </button>
            </form>
          </div>
        </div>
      )}

      <main className="main-content">
        {/* Navigation Buttons */}
        <section className="scheduler-section">
          <div className="cta-buttons">
            <Link to="/calendar" className="cta-button">
              📅 Calendar
            </Link>
            <Link to="/team" className="cta-button">
              👥 Team Hub
            </Link>
            <Link to="/appointments" className="cta-button">
              📋 Appointments
            </Link>
            <Link to="/points" className="cta-button">
              ⭐ Point Tracker
            </Link>
            {currentUser && (
              <>
                {/* Admin-only buttons */}
                {canAccessTaskManager(currentUserData) && (
                  <Link to="/tasks" className="cta-button">
                    ✓ Task Manager
                  </Link>
                )}
                {canAccessLessonPlanner(currentUserData) && (
                  <Link to="/planner" className="cta-button">
                    📚 Lesson Planner
                  </Link>
                )}
                {canAccessDailySummary(currentUserData) && (
                  <Link to="/daily-summary-manager" className="cta-button">
                    📺 Class Daily Summary
                  </Link>
                )}
              </>
            )}
          </div>
          {!currentUser && (
            <p className="login-prompt">Login to access the dashboard</p>
          )}
        </section>

        {/* Team Hub Section - Only for Officers, Varsity, and Admins */}
        {currentUser && canAccessTeamDashboard(currentUserData) && (
          <section className="team-hub-section">
            <h2>👥 Team Hub</h2>

            <div className="team-hub-content">
              {/* Tasks Section */}
              <div className="team-hub-tasks">
                <h3>📋 Team Tasks</h3>

                <form onSubmit={handleCreateTask} className="team-task-form">
                  <input
                    type="text"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder="Add a new task..."
                    className="team-task-input"
                  />
                  <button type="submit" className="team-add-btn">+ Add</button>
                </form>

                <div className="team-tasks-list">
                  {tasks.length === 0 ? (
                    <p className="team-no-data">No tasks yet. Create one above!</p>
                  ) : (
                    tasks.map(task => (
                      <div key={task.id} className={`team-task-item ${task.completed ? 'completed' : ''}`}>
                        <div className="team-task-checkbox-container">
                          <input
                            type="checkbox"
                            checked={task.completed}
                            onChange={() => handleToggleTask(task.id, task.completed)}
                            className="team-task-checkbox"
                          />
                          {task.completed && task.completedBy && (
                            <div className="team-task-completed-avatar" title={`Completed by ${task.completedBy.name || 'Unknown'}`}>
                              {task.completedBy.initials}
                            </div>
                          )}
                        </div>
                        <span className="team-task-title">{task.title}</span>

                        <div className="team-task-meta">
                          <div className="team-task-creator-avatar">
                            {task.createdByInitials}
                          </div>
                          <button
                            onClick={() => handleDeleteTask(task)}
                            className="team-delete-task-btn"
                            title="Delete task"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Messages Section */}
              <div className="team-hub-messages">
                <h3>💬 Team Messages</h3>

                <div className="team-messages-list">
                  {organizeMessageThreads().map(message => (
                    <div key={message.id} className="team-message-thread">
                      {/* Parent Message */}
                      <div className={`team-message-item ${replyingTo?.id === message.id ? 'replying-to' : ''}`}>
                        {replyingTo?.id === message.id && (
                          <div className="team-replying-to-indicator">Replying to this message</div>
                        )}
                        <div className="team-message-header">
                          <div className="team-message-avatar">
                            {message.createdByInitials}
                          </div>
                          <div className="team-message-info">
                            <strong className="team-message-author">{message.createdByName}</strong>
                            <span className="team-message-time">{formatTimestamp(message.createdAt)}</span>
                          </div>
                          <button
                            onClick={() => handleReply(message)}
                            className="team-reply-message-btn"
                            title="Reply to message"
                          >
                            ↩
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(message)}
                            className="team-delete-message-btn"
                            title="Delete message"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="team-message-content">{message.content}</div>
                      </div>

                      {/* Reply Messages */}
                      {message.replies && message.replies.length > 0 && (
                        <div className="team-message-replies">
                          {message.replies.map(reply => (
                            <div key={reply.id} className="team-message-item team-message-reply">
                              <div className="team-message-header">
                                <div className="team-message-avatar team-message-avatar-small">
                                  {reply.createdByInitials}
                                </div>
                                <div className="team-message-info">
                                  <strong className="team-message-author">{reply.createdByName}</strong>
                                  <span className="team-message-time">{formatTimestamp(reply.createdAt)}</span>
                                </div>
                                <button
                                  onClick={() => handleDeleteMessage(reply)}
                                  className="team-delete-message-btn"
                                  title="Delete message"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="team-message-content">{reply.content}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <form onSubmit={handleCreateMessage} className="team-message-form">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={replyingTo ? "Type your reply..." : "Type a message..."}
                    className="team-message-input"
                    rows="3"
                  />
                  <div className="team-message-form-actions">
                    <button type="submit" className="team-post-btn">{replyingTo ? 'Reply' : 'Post'}</button>
                    {replyingTo && (
                      <button
                        type="button"
                        onClick={cancelReply}
                        className="team-cancel-reply-btn-inline"
                        title="Cancel reply"
                      >
                        Cancel Reply
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Documents Section - Only for Admin and Officers */}
              {(isAdmin(currentUserData) || currentUserData?.role === 'Officer') && (
                <div className="team-hub-documents">
                  <h3>📁 Important Documents</h3>
                  <p className="documents-description">Upload and share important team documents (accessible to all officers and admins)</p>

                  <div className="upload-section">
                    <input
                      type="file"
                      onChange={handleUploadDocument}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                      id="document-upload"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="document-upload" className="upload-btn">
                      {uploadingDocument ? '⏳ Uploading...' : '📤 Upload Document'}
                    </label>
                  </div>

                  <div className="team-documents-list">
                    {documents.length === 0 ? (
                      <p className="team-no-data">No documents uploaded yet.</p>
                    ) : (
                      documents.map(doc => (
                        <div key={doc.id} className="team-document-item">
                          <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="document-link">
                            <div className="document-icon">📄</div>
                            <div className="document-info">
                              <div className="document-name">{doc.fileName}</div>
                              <div className="document-meta">
                                Uploaded by {doc.uploadedByName} • {doc.uploadedAt?.toDate?.().toLocaleDateString()}
                              </div>
                            </div>
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc)}
                            className="team-delete-document-btn"
                            title="Delete document"
                          >
                            ✕
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Weekly Schedule Section - Only for Class Novice and non-logged-in users */}
        {!loading && canSeeCurriculum(currentUserData) && (
          <section className="daily-schedule-section">
            <h2>This Week's Debate 1 Curriculum</h2>

            {/* Desktop Grid View - All 6 days */}
            <div className="week-grid desktop-only">
              {Object.entries(weeklyLessons).map(([dayName, dayData]) => {
                const isToday = dayData.date && dayData.date.toDateString() === new Date().toDateString();
                const dayNumber = dayData.date?.getDate();
                const monthName = dayData.date?.toLocaleDateString('en-US', { month: 'short' });
                const isSaturday = dayName === 'Saturday';

                return (
                  <div key={dayName} className={`week-day-card ${isToday ? 'today' : ''} ${isSaturday ? 'saturday-card' : ''}`}>
                    <div className="week-day-header">
                      <div className="week-day-name">{dayName.substring(0, 3).toUpperCase()}</div>
                      <div className="week-day-number">
                        {monthName} {dayNumber}
                      </div>
                    </div>
                    <div className="week-day-lessons">
                      {isSaturday ? (
                        // Saturday special content - show tournaments
                        dayData.tournaments && dayData.tournaments.length > 0 ? (
                          <div className="tournament-info">
                            {dayData.tournaments.map(tournament => (
                              <div key={tournament.id} className="tournament-item">
                                <div className="tournament-icon">🏆</div>
                                <div className="tournament-details">
                                  <div className="tournament-name">{tournament.title}</div>
                                  <div className="tournament-date">
                                    {new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-weekend-events">
                            <div className="no-events-icon">🎉</div>
                            <div className="no-events-text">No Events This Weekend!</div>
                          </div>
                        )
                      ) : (
                        // Weekday content - show lessons
                        !dayData.lessons || dayData.lessons.length === 0 ? (
                          <div className="week-no-lessons">No lessons</div>
                        ) : (
                          dayData.lessons.map(lesson => (
                            <div key={lesson.id} className="week-lesson-item">
                              <div className="week-lesson-content">
                                <div className="week-lesson-title">{lesson.title}</div>
                              </div>
                              {lesson.resources && lesson.resources.length > 0 && (
                                <button
                                  className="week-lesson-resource-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (lesson.resources.length === 1) {
                                      window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                    } else {
                                      // Show first resource or navigate to lesson planner
                                      window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                    }
                                  }}
                                  title={lesson.resources.length === 1 ? 'Open resource' : `${lesson.resources.length} resources`}
                                >
                                  🔗
                                  {lesson.resources.length > 1 && <span className="resource-badge">{lesson.resources.length}</span>}
                                </button>
                              )}
                            </div>
                          ))
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile Carousel View - One day at a time */}
            <div className="curriculum-carousel mobile-only">
              {(() => {
                const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const dayName = weekdays[currentDayIndex];
                const dayData = weeklyLessons[dayName];

                if (!dayData) return null;

                const isToday = dayData.date && dayData.date.toDateString() === new Date().toDateString();
                const dayNumber = dayData.date?.getDate();
                const monthName = dayData.date?.toLocaleDateString('en-US', { month: 'short' });
                const isSaturday = dayName === 'Saturday';

                return (
                  <>
                    <button
                      className="carousel-nav carousel-prev"
                      onClick={() => setCurrentDayIndex(prev => Math.max(0, prev - 1))}
                      disabled={currentDayIndex === 0}
                      aria-label="Previous day"
                    >
                      ‹
                    </button>

                    <div className={`week-day-card-large ${isToday ? 'today' : ''} ${isSaturday ? 'saturday-card' : ''}`}>
                      <div className="week-day-header">
                        <div className="week-day-name">{dayName}</div>
                        <div className="week-day-number">
                          {monthName} {dayNumber}
                        </div>
                      </div>
                      <div className="week-day-lessons">
                        {isSaturday ? (
                          // Saturday special content - show tournaments
                          dayData.tournaments && dayData.tournaments.length > 0 ? (
                            <div className="tournament-info">
                              {dayData.tournaments.map(tournament => (
                                <div key={tournament.id} className="tournament-item">
                                  <div className="tournament-icon">🏆</div>
                                  <div className="tournament-details">
                                    <div className="tournament-name">{tournament.title}</div>
                                    <div className="tournament-date">
                                      {new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-weekend-events">
                              <div className="no-events-icon">🎉</div>
                              <div className="no-events-text">No Events This Weekend!</div>
                            </div>
                          )
                        ) : (
                          // Weekday content - show lessons
                          !dayData.lessons || dayData.lessons.length === 0 ? (
                            <div className="week-no-lessons">No lessons</div>
                          ) : (
                            dayData.lessons.map(lesson => (
                              <div key={lesson.id} className="week-lesson-item">
                                <div className="week-lesson-content">
                                  <div className="week-lesson-title">{lesson.title}</div>
                                </div>
                                {lesson.resources && lesson.resources.length > 0 && (
                                  <button
                                    className="week-lesson-resource-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (lesson.resources.length === 1) {
                                        window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                      } else {
                                        // Show first resource or navigate to lesson planner
                                        window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                      }
                                    }}
                                    title={lesson.resources.length === 1 ? 'Open resource' : `${lesson.resources.length} resources`}
                                  >
                                    🔗
                                    {lesson.resources.length > 1 && <span className="resource-badge">{lesson.resources.length}</span>}
                                  </button>
                                )}
                              </div>
                            ))
                          )
                        )}
                      </div>

                      {/* Day indicators */}
                      <div className="carousel-indicators">
                        {weekdays.map((day, idx) => (
                          <button
                            key={day}
                            className={`indicator ${idx === currentDayIndex ? 'active' : ''}`}
                            onClick={() => setCurrentDayIndex(idx)}
                            aria-label={`Go to ${day}`}
                          >
                            {day.substring(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      className="carousel-nav carousel-next"
                      onClick={() => setCurrentDayIndex(prev => Math.min(5, prev + 1))}
                      disabled={currentDayIndex === 5}
                      aria-label="Next day"
                    >
                      ›
                    </button>
                  </>
                );
              })()}
            </div>
          </section>
        )}

        {/* Upcoming Calendar Events Section */}
        {!loading && (
          <section className="upcoming-events-section">
            <h2>
              <CalendarIcon size={28} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '10px' }} />
              Upcoming Calendar Events (Next 4 Weeks)
            </h2>
            {calendarEvents.length === 0 ? (
              <div className="empty-state">
                <p>No upcoming events</p>
              </div>
            ) : (
              <div className="events-list">
                {calendarEvents.map(event => {
                  const eventStartDate = parseDateLocal(event.date);
                  const eventEndDate = event.endDate ? parseDateLocal(event.endDate) : null;

                  // Format date range for multi-day events
                  let dateDisplay = eventStartDate?.toLocaleDateString('en-US', { weekday: 'long' });
                  if (eventEndDate && eventEndDate.getTime() !== eventStartDate.getTime()) {
                    const endDay = eventEndDate.toLocaleDateString('en-US', { weekday: 'long' });
                    dateDisplay = `${dateDisplay} - ${endDay}`;
                  }

                  return (
                    <div key={event.id} className="event-card calendar-event">
                      <div className="event-date-badge" style={{ background: event.color || '#3b82f6' }}>
                        <div className="event-month">
                          {eventStartDate?.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                        <div className="event-day">
                          {eventStartDate?.getDate()}
                          {eventEndDate && eventEndDate.getTime() !== eventStartDate.getTime() && (
                            <>-{eventEndDate.getDate()}</>
                          )}
                        </div>
                      </div>
                      <div className="event-details">
                        <h3 className="event-title">{event.title}</h3>
                        <p className="event-day-name">
                          {dateDisplay}
                          {event.startTime && event.endTime && (
                            <span className="event-time"> • {event.startTime} - {event.endTime}</span>
                          )}
                        </p>
                        {event.description && (
                          <p className="event-description">{event.description}</p>
                        )}
                        {event.type && (
                          <span className="event-type-badge">{event.type}</span>
                        )}
                        {(event.tournamentUrl || event.scheduleUrl) && (
                          <div className="event-links">
                            {event.tournamentUrl && (
                              <a href={event.tournamentUrl} target="_blank" rel="noopener noreferrer" className="event-link tournament-link">
                                🏆 Tournament Info
                              </a>
                            )}
                            {event.scheduleUrl && (
                              <a href={event.scheduleUrl} target="_blank" rel="noopener noreferrer" className="event-link schedule-link">
                                📅 View Schedule
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Upcoming Lesson Planner Events Section */}
        {!loading && upcomingEvents.length > 0 && (
          <section className="upcoming-events-section">
            <h2>Upcoming Lesson Planner Events (Next 4 Weeks)</h2>
            <div className="events-list">
              {upcomingEvents.map(event => {
                const eventDate = typeof event.date === 'string'
                  ? new Date(event.date)
                  : event.date.toDate ? event.date.toDate() : null;

                return (
                  <div key={event.id} className="event-card">
                    <div className="event-date-badge">
                      <div className="event-month">
                        {eventDate?.toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="event-day">
                        {eventDate?.getDate()}
                      </div>
                    </div>
                    <div className="event-details">
                      <h3 className="event-title">{event.title}</h3>
                      <p className="event-day-name">
                        {eventDate?.toLocaleDateString('en-US', { weekday: 'long' })}
                      </p>
                      {event.description && (
                        <p className="event-description">{event.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Mr. Willeby. All rights reserved.</p>
      </footer>
    </div>
  );
}

function AppContent() {
  const { needsPasswordChange, currentUser } = useAuth();

  return (
    <>
      {/* Show password change modal if user needs to change password */}
      {currentUser && needsPasswordChange && <PasswordChangeModal />}

      <Router>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <TaskList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={<Calendar />}
            />
            <Route
              path="/team"
              element={
                <ProtectedRoute>
                  <TeamDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appointments"
              element={<Appointments />}
            />
            <Route
              path="/lessons"
              element={
                <ProtectedRoute>
                  <LessonPlanner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/units"
              element={
                <ProtectedRoute>
                  <Units />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/lessons"
              element={
                <ProtectedRoute>
                  <Lessons />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/classes"
              element={
                <ProtectedRoute>
                  <Classes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/classrooms"
              element={
                <ProtectedRoute>
                  <Classrooms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/calendar"
              element={
                <ProtectedRoute>
                  <PlannerCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/schedule"
              element={
                <ProtectedRoute>
                  <WeeklySchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planner/classes/:classId/scope"
              element={
                <ProtectedRoute>
                  <ScopeAndSequence />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/points"
              element={<PointTracker />}
            />
            <Route
              path="/planner/assignments"
              element={
                <ProtectedRoute>
                  <Assignments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-summary-manager"
              element={
                <ProtectedRoute>
                  <DailySummaryManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-summary"
              element={
                <ProtectedRoute>
                  <DailySummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/big-screen-view"
              element={
                <ProtectedRoute>
                  <BigScreenView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/host-availability"
              element={
                <ProtectedRoute>
                  <HostAvailability />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </>
    );
  }

  function App() {
    return (
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    );
  }

export default App;
